import React, {Component} from "react";
import {Control, Field, Icon} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {initDeepFakeDetectionJob, collectDeepFakeDetectionJob} from "../../../api/models/DeepFakeDetection";

interface IDeepFakeDetectionModelState {
    loading: boolean;
    processingSecondsEstimate: number;
    loadingEstimate: number;
    file: any;
    videoUrl: any;
    requestedJobId: string;
    result: string;
    fileErrorMessage: string;
    errorMessage: string;
}

class DeepFakeDetectionModel extends Component<{}, IDeepFakeDetectionModelState> {
    public ALLOWED_FILE_TYPES: any = ['video/mp4'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;
    public loadingInterval: any = null;

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            loadingEstimate: null,
            file: null,
            processingSecondsEstimate: null,
            videoUrl: "",
            requestedJobId: "",
            result: "",
            fileErrorMessage: "",
            errorMessage: "",
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.DEEPFAKE_DETECTION);
    }

    public recordVideoDuration = (file : any) => {
        const reader = new FileReader();
        reader.onload = () => {
            const media = new Audio(reader.result as string);
            media.onloadedmetadata = () => {
                 this.setState({ // Rough estimate: 30% of video duration for long videos, but 10s overhead for short videos.
                    processingSecondsEstimate: Math.ceil(media.duration * 0.3 + 10),
                 });
            };
        };
        reader.readAsDataURL(file);
    }

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: "",
            errorMessage: "",
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {
                const url = URL.createObjectURL(file);
                this.recordVideoDuration(file);

                this.setState({
                    file,
                    videoUrl: url
                });
            } else {
                this.setState({
                    file: null,
                    videoUrl: "",
                    fileErrorMessage: "The model only accepts MP4 format files."
                });
            }
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();
        clearInterval(this.loadingInterval);

        if (!this.state.file || this.state.fileErrorMessage) {
            if (!this.state.file) {
                this.setState({
                    fileErrorMessage: "Please select video."
                });
            }

            return;
        }

        this.setState(prevState => ({
            requestedJobId: "",
            loading: true,
            loadingEstimate: prevState.processingSecondsEstimate,
            errorMessage: ""
        }));

        let errorOccurred = false;

        this.loadingInterval = setInterval(() => {
            this.setState(prevState => ({
                loadingEstimate: prevState.loadingEstimate > 0 ? prevState.loadingEstimate - 1 : 0,
            }) );
            if (this.state.loadingEstimate % 2 === 0) { // Only call collectJob every 2 seconds to limit requests.
                this.collectJob();
            }
        }, 1000);

        const response = await initDeepFakeDetectionJob(this.state.file).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process video."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            this.setState({
                requestedJobId: response.data
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.DEEPFAKE_DETECTION);
    };

    // Called on interval after submit button is pressed.
    // This functions polls the backend to see if the previously requested job is yet complete.
    public collectJob = async () => {
        if (!this.state.requestedJobId) {
            return;
        }

        const response = await collectDeepFakeDetectionJob(this.state.requestedJobId).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process video."
                });
            }
            return; // Return on error
        });

        if (response && response.data) {
            if (this.state.loading) { // Don't update if user already pressed the reset button
                const result = parseFloat(response.data);
                if (isNaN(result)) {
                    return; // Intermittent status rather than final result.
                }

                clearInterval(this.loadingInterval);
                this.setState({
                    loading: false,
                    result: this.getPredictionText(result)
                });
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process video."
            });
        }
    };

    public resetState = async (e: any) => {
        e.target.blur();
        clearInterval(this.loadingInterval);

        this.setState({
            loading: false,
            loadingEstimate: null,
            file: null,
            videoUrl: "",
            result: "",
            fileErrorMessage: "",
            errorMessage: "",
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public getLoadingDurationEstimateText = () => {
        const mins = Math.floor(this.state.loadingEstimate / 60);
        const secs = this.state.loadingEstimate % 60;
        return "" + (mins ? mins + " minute" + (mins === 1 ? " " : "s ") : "")
                  + (secs ? secs + " second" + (secs === 1 ? " " : "s") : "")
    }

    public getPredictionText = (score : number) => {
        if (score > 0.45) {
            return "DeepFake detected (confidence level: " + (score > 0.70 ? "high" : "low") + ")";
        } else {
            return "No DeepFake detected (confidence level: " + (score < 0.30 ? "high" : "low") + ")";
        }
    };

    public componentWillUnmount() {
        clearInterval(this.loadingInterval);
    }

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                { !this.state.file ? <div>
                                    <SectionName>Select MP4 Video:</SectionName>
                                    <input
                                        ref={(el) => this.fileInputRef = el}
                                        type="file"
                                        accept={this.ALLOWED_FILE_TYPES_STR}
                                        onChange={this.handleFileChange}
                                        style={this.fileInputStyle}
                                    />
                                </div> : <></> }
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                <div style={{
                                                margin: '20px',
                                                textAlign: 'center',
                                                position: 'relative'
                                            }}>

                                    <div>
                                    { !this.state.fileErrorMessage && this.state.videoUrl ?
                                        <video controls={true}>
                                            <source src={this.state.videoUrl} />
                                        </video>
                                        : <></> }
                                    </div>

                                    <div>
                                        { this.state.result ?
                                            <h1> Analysis: {this.state.result} </h1> : <></>
                                        }
                                    </div>

                                </div>
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit} disabled={this.state.loading}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                                <div>Estimated time remaining: {this.getLoadingDurationEstimateText()}</div>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.errorMessage ?
                                <div>{this.state.errorMessage}</div> : <></>
                        }
                        <Spacer/>
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default DeepFakeDetectionModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/DeepFakeDetection/DeepFakeDetectionModel.tsx